import React, { useState } from 'react';
import { Image } from 'lucide-react';
import { useTheme } from 'components/core/theme-provider';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'components/ui/skeleton';

interface SearchRelatedImageListProps {
    imageList: any;
    messageId?: string;
    isSkeletonShowForImages?: boolean;
}

export interface RelativeSearchImage {
    title: string;
    imgSrc: string | null;
    url: string | null;
    iframeSrc: string | null;
}

export default function SearchRelatedImageList({ imageList, messageId, isSkeletonShowForImages }: SearchRelatedImageListProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Use messageId to create unique state key
    const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
    const { t } = useTranslation();
    const { theme } = useTheme();

    const cardClass = theme === 'dark'
        ? "bg-white-25 hover:bg-primary-600 group transition duration-200 rounded-lg p-2 flex-col space-y-2 font-medium shado cursor-pointer"
        : "bg-gray-100 hover:bg-gray-200 transition duration-200 rounded-lg p-2 flex-col space-y-2 font-medium cursor-pointer";

    const imagesList = imageList?.data || [];
    const remainingCount = Math.max(imagesList.length - 3, 0);

    // Create unique key combining messageId and image index
    const getImageKey = (index: number) => `${messageId}-${index}`;

    const handleImageLoad = (index: number) => {
        const key = getImageKey(index);
        setLoadedImages(prev => new Set(prev).add(key));
    };

    const isImageLoaded = (index: number) => {
        return loadedImages.has(getImageKey(index));
    };

    const openInNewTab = (imgSrc: string | null) => {
        if (!imgSrc) return;
        const printWindow = window.open(imgSrc, '_blank');
        printWindow?.document.close();
    };

    if (isSkeletonShowForImages) {
        return (
            <div className="w-full p-4">
                <div className="flex items-center gap-2 mb-4">
                    <Skeleton className="h-5 w-5 rounded" />
                    <Skeleton className="h-6 w-24" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="rounded-lg p-2 flex-col space-y-2">
                            <Skeleton className="w-full h-14 rounded-lg" />
                            <Skeleton className="h-4 w-full" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (imagesList.length) {
        return (
            <div className="w-full p-4">
                {imagesList.length ? <div className="flex items-center gap-2 mb-4">
                    <Image size={20} />
                    <span className="text-lg fw-500">{t('IMAGES')}</span>
                </div> : null}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {imagesList.slice(0, 3).map((image: RelativeSearchImage, index: number) => (
                        <div
                            key={index}
                            className={cardClass}
                            onClick={() => openInNewTab(image.imgSrc)}
                        >
                            <div className="relative w-full h-14 mb-2">
                                {!isImageLoaded(index) && (
                                    <Skeleton className="absolute inset-0 rounded-lg" />
                                )}
                                <img
                                    src={image.imgSrc || ''}
                                    alt={`Gallery ${index + 1}`}
                                    className={`w-full h-14 object-cover rounded-lg ${isImageLoaded(index) ? 'opacity-100' : 'opacity-0'
                                        }`}
                                    onLoad={() => handleImageLoad(index)}
                                />
                            </div>
                            <p className="text-xs truncate text-center">{image.title}</p>
                        </div>
                    ))}

                    {remainingCount > 0 && (
                        <button onClick={() => setIsModalOpen(true)} className={cardClass}>
                            <div className="flex flex-row items-center space-x-1 justify-center">
                                {imagesList.slice(3, 6).map((image: RelativeSearchImage, index: number) => (
                                    <img
                                        key={index}
                                        width="16"
                                        height="16"
                                        alt="favicon"
                                        className="rounded-lg h-4 w-4"
                                        src={image.imgSrc || ''}
                                    />
                                ))}
                            </div>
                            <p className="text-xs text-gray-500 group-hover:text-typography-white">
                                {t("VIEW")} {imagesList.length - 3} {t("MORE")}
                            </p>
                        </button>
                    )}
                </div>

                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                        <div className="bg-white rounded-xl p-6 w-11/12 max-w-3xl shadow-xl overflow-y-auto max-h-[90vh]">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold">{t('ALL_IMAGES')}</h2>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="text-xl hover:opacity-70"
                                >
                                    ✖
                                </button>
                            </div>

                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                                {imagesList.map((image: RelativeSearchImage, index: number) => (
                                    <img
                                        key={index}
                                        src={image.imgSrc || ''}
                                        alt={`Image ${index + 1}`}
                                        className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:opacity-80 transition"
                                        onClick={() => openInNewTab(image.imgSrc)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    } else {
        return null;
    }
}